import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { InternalToastProps } from "../toasts/core/types";
import { Observable } from "#src/utils/observer";
import { iconByNotificationType } from "./enum";
import { XClose } from "@untitled-ui/icons-react";
import { useTranslation } from "react-i18next";

interface INotificationBannerProps {
  toast: InternalToastProps;
  observable: Observable<InternalToastProps>;
}

/** Just notification's UI. */
export const NotificationBanner = ({
  toast,
  observable,
}: INotificationBannerProps) => {
  const { t } = useTranslation("common", { keyPrefix: "v2.notification" });
  const [mounted, setMounted] = useState<boolean>(false);

  const ref = useRef<HTMLDivElement>(null);

  // --------------- COMPUTED ---------------

  const toastMode: "normal" | "gradient" | "dark" = useMemo(() => {
    if (["peach"].includes(toast.type)) {
      return "gradient";
    }
    return "normal";
  }, [toast.type]);

  // --------------- METHODS ---------------

  const dismissToast = useCallback(
    (dismissedToast: InternalToastProps) => {
      observable.notify({ ...dismissedToast, status: "dismissed" });
    },
    [observable]
  );

  const destroyToast = useCallback(
    (destroyingToast: InternalToastProps) => {
      observable.notify({ ...destroyingToast, status: "destroyed" });
    },
    [observable]
  );

  // --------------- EFFECTS ---------------

  useEffect(() => {
    // Trigger enter animation
    setMounted(true);
  }, []);

  // --------------- RENDER ---------------

  return (
    <div
      ref={ref}
      style={
        !mounted
          ? // The first state: before toast is mounted
            {
              transform: "translate(0, 100%)",
              height: ref.current?.offsetHeight,
              paddingTop: "1rem",
              paddingBottom: "1rem",
              borderWidth: "1px",
            }
          : toast.status === "dismissed"
            ? // The last state before toast is destroyed
              {
                height: 0,
                minHeight: 0,
                opacity: 0,
                paddingTop: 0,
                paddingBottom: 0,
                borderWidth: 0,
              }
            : // The stable state
              {
                height: ref.current?.offsetHeight,
                paddingTop: "1rem",
                paddingBottom: "1rem",
                borderWidth: "1px",
              }
      }
      className="v2 relative px-4 w-[80vw] flex items-start gap-4 rounded-lg duration-300 ease-out overflow-hidden border-gray-v2-300 bg-white shadow-xs"
      // TODO: this is not consistent and might be easily broken
      onTransitionEnd={(e) => {
        if (e.target === e.currentTarget) {
          if (e.propertyName === "height") {
            destroyToast(toast);
          }
        }
      }}
    >
      {iconByNotificationType[toast.type]}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-gray-v2-700 min-h-[1em] break-words">
            {toast.message}
          </span>
          {toast.detail ? (
            <span
              className={`text-sm min-h-[1em] break-words ${toastMode === "gradient" ? "text-white" : "text-gray-v2-600"}`}
            >
              {toast.detail}
            </span>
          ) : null}
        </div>
        <button
          className="text-sm font-semibold text-brand-v2-700 hover:text-brand-v2-800 w-fit"
          onClick={() => {
            dismissToast(toast);
          }}
        >
          {t("button.ok")}
        </button>
      </div>
      <button
        className={`absolute m-2 top-2 right-2 flex text-gray-v2-400 ${
          toastMode === "gradient"
            ? "bg-transparent hover:bg-transparent"
            : undefined
        }`}
        onClick={() => {
          dismissToast(toast);
        }}
      >
        <XClose className="h-5 w-5 min-w-5" />
      </button>
    </div>
  );
};
